import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { coreApi } from "api/core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PrintIcon from "@material-ui/icons/Print";
import { PATHS } from "util/appConstants";
import { useHistory } from "react-router-dom";
import { usePermission } from "hooks/usePermission";
import { shouldShowScanCodes } from "util/deliveryTimingConfigUtils";
import { useSelector } from "react-redux";
import { selectUser } from "redux/slices/userSlice";
import { getRouteScanPrintAvailability } from "util/scan-code-print";


const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiPaper-root": {
      backgroundColor: "#121212",
      borderRadius: "0px",
      color: "white",
      fontFamily: "roboto !important",
    },
  },
  listItem: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    "&:last-child": {
      borderBottom: "none",
    },
    background: "#232323",
    marginBottom: 4,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
  },
  address: {
    color: "#ADADAD",
    fontSize: "0.875rem",
  },
  dragHandle: {
    cursor: "grab",
    marginRight: 12,
    color: "#6F9CEB",
    fontSize: 22,
  },
  editButton: {
    color: "#6F9CEB",
    padding: 4,
  },
  accordionSummary: {
    padding: "4px 12px",
    minHeight: "48px !important",
    "&.Mui-expanded": {
      minHeight: "48px !important",
    },
    color: "white"
  },
  accordionSummaryContent: {
    margin: "8px 0 !important",
    alignItems: "center",
    "&.Mui-expanded": {
      margin: "8px 0 !important",
    },
  },
  accordionDetails: {
    padding: 0,
    display: "block",
    width: "100%",
  },
  orderItem: {
    padding: "8px 12px",
    backgroundColor: "#1A1A1A",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  orderNumber: {
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  deliveryNote: {
    fontSize: "0.85rem",
    color: "#ADADAD",
    marginTop: 2,
  },
  expressChip: {
    backgroundColor: "#FFC107",
    color: "#000",
    height: 20,
    fontSize: "0.7rem",
    marginLeft: 8,
  },
  packages: {
    backgroundColor: "#6F9CEB",
    color: "#fff",
    borderRadius: "4px",
    padding: "2px 6px",
    fontSize: "0.75rem",
    marginRight: 8,
    display: "inline-block",
  },
  editOrderButton: {
    color: "#6F9CEB",
    padding: 4,
    "&.Mui-disabled": {
      color: "#6F9CEB",
      opacity: 0.6,
    },
  },
  reorderCheckbox: {
    color: "#6F9CEB !important",
    "&.Mui-disabled": {
      color: "#6F9CEB !important",
      opacity: 0.6,
    },
  },
  reorderLabelDisabled: {
    color: "#6F9CEB !important",
    opacity: 0.6,
  },
  expandIcon: {
    color: "#6F9CEB",
  },
  expandIconButton: {
    color: "#6F9CEB",
    "&:hover": {
      color: "#4a8fd4",
    },
  },
  printButton: {
    color: "#6F9CEB",
    "& .MuiButton-startIcon": {
      color: "#6F9CEB",
    },
    "&.Mui-disabled": {
      color: "#6F9CEB",
      opacity: 0.6,
    },
    "&.Mui-disabled .MuiButton-startIcon": {
      color: "#6F9CEB",
      opacity: 0.6,
    },
  },
}));

const ReorderStopsModal = ({ open, onClose, routeId, onSave, onEditStop }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const user = useSelector(selectUser);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stops, setStops] = useState([]);
  const [nonCustomerStops, setNonCustomerStops] = useState([]);
  const [expandedStop, setExpandedStop] = useState(null);
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [isRouteStarted, setIsRouteStarted] = useState(false);
  const history = useHistory();

  const hasReorderPermission = usePermission("routes.reorder_stops");

  useEffect(() => {
    if (open && routeId) {
      setLoading(true);
      coreApi
        .fetch(`/routes/${routeId}`)
        .then((data) => {
          setRoute(data);
          const customers = (data.pathway || []).filter((stop) => stop.id !== null);
          const nonCustomers = (data.pathway || []).filter((stop) => stop.id === null);
          setStops(customers);
          setNonCustomerStops(nonCustomers);
          setIsRouteStarted(data?.verified_at ?? !!data.start_date);
        })
        .finally(() => setLoading(false));
    } else if (!open) {
      setRoute(null);
      setStops([]);
      setNonCustomerStops([]);
      setReorderEnabled(false);
    }
  }, [open, routeId, hasReorderPermission]);

  const handleDragEnd = (result) => {
    if (!result.destination || !reorderEnabled) return;
    const reordered = Array.from(stops);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setStops(reordered);
  };

  const handleSave = () => {
    if (!route) return;

    let customerIdx = 0;
    const allStops = (route.pathway || []).map((originalStop) => {
      if (originalStop.id === null) {
        return originalStop;
      }
      const reorderedStop = stops[customerIdx];
      customerIdx++;
      return {
        ...(reorderedStop || originalStop),
        tour_position: customerIdx,
      };
    });

    const updatedRoute = {
      ...route,
      pathway: allStops,
    };
    onSave(updatedRoute);
  };

  const handleEditOrder = (order) => {
    onClose();

    const queryParams = new URLSearchParams({
      from: 'reorderStopsModal',
      routeId: routeId,
      fromPage: history.location.pathname
    }).toString();

    history.push(`${PATHS.orders.edit.replace(":id", order.id)}?${queryParams}`);

    if (onEditStop) {
      onEditStop({
        orderId: order.id,
        from: 'reorderStopsModal'
      });
    }
  };

  const handleAccordionChange = (stopId) => (event, isExpanded) => {
    setExpandedStop(isExpanded ? stopId : null);
  };

  const { canPrintAllQr, canPrintSupplierQr } = useMemo(
    () => getRouteScanPrintAvailability(route?.pathway, user?.supplier_location_id),
    [route?.pathway, user?.supplier_location_id]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth className={classes.dialog}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t("Stops Information")}</Typography>
          {shouldShowScanCodes(route?.delivery_timing_config) ?
            (
              <Box display="flex" gap={1}>
                <Tooltip
                  title={t("qr_print_unavailable_atbas_scan_import")}
                  disableHoverListener={canPrintAllQr}
                  disableFocusListener={canPrintAllQr}
                  disableTouchListener={canPrintAllQr}
                >
                  <span style={{ display: "inline-flex" }}>
                    <Button
                      color="primary"
                      startIcon={<PrintIcon />}
                      disabled={!canPrintAllQr}
                      className={classes.printButton}
                      onClick={() => {
                        if (!canPrintAllQr) return;
                        const link = document.createElement("a");
                        link.href = `${import.meta.env.VITE_API}routes/${routeId}/scan-codes?scope=all&taira=${localStorage.getItem("token")}`;
                        link.target = "_blank";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      {t("All")}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={t("qr_print_unavailable_atbas_scan_import")}
                  disableHoverListener={canPrintSupplierQr}
                  disableFocusListener={canPrintSupplierQr}
                  disableTouchListener={canPrintSupplierQr}
                >
                  <span style={{ display: "inline-flex" }}>
                    <Button
                      color="primary"
                      startIcon={<PrintIcon />}
                      disabled={!canPrintSupplierQr}
                      className={classes.printButton}
                      onClick={() => {
                        if (!canPrintSupplierQr) return;
                        const link = document.createElement("a");
                        link.href = `${import.meta.env.VITE_API}routes/${routeId}/scan-codes?scope=supplier_location&taira=${localStorage.getItem("token")}`;
                        link.target = "_blank";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      {t("Supplier Location")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>) : <></>
          }
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading || !route ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <>
            {hasReorderPermission && (
              <Box mb={2} mt={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={reorderEnabled}
                      onChange={(e) => setReorderEnabled(e.target.checked)}
                      color="primary"
                      className={classes.reorderCheckbox}
                      disabled={isRouteStarted}
                    />
                  }
                  label={t("Reorder Stops")}
                  style={isRouteStarted ? { color: "#6F9CEB", opacity: 0.6 } : undefined}
                  classes={{
                    label: isRouteStarted ? classes.reorderLabelDisabled : undefined,
                  }}
                />
              </Box>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="stops-list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {stops.map((stop, index) => (
                      <Draggable key={stop.id} draggableId={stop.id.toString()} index={index} isDragDisabled={!reorderEnabled}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              boxShadow: snapshot.isDragging ? "0 2px 8px #000" : "none",
                              marginBottom: 8,
                            }}
                          >
                            <Accordion
                              expanded={expandedStop === stop.id}
                              onChange={handleAccordionChange(stop.id)}
                              TransitionProps={{ unmountOnExit: true }}
                              className={classes.listItem}
                              style={{
                                display: "block",
                                width: "100%"
                              }}
                            >
                              <AccordionSummary
                                aria-controls={`stop-${stop.id}-content`}
                                id={`stop-${stop.id}-header`}
                                className={classes.accordionSummary}
                                classes={{
                                  content: classes.accordionSummaryContent,
                                  expandIcon: classes.expandIconButton,
                                }}
                                expandIcon={null}
                              >
                                <span
                                  {...provided.dragHandleProps}
                                  className={classes.dragHandle}
                                  style={{
                                    cursor: reorderEnabled ? "grab" : "default",
                                    opacity: reorderEnabled ? 1 : 0.5
                                  }}
                                >
                                  &#9776;
                                </span>
                                <div style={{ flex: 1 }}>
                                  <Typography variant="body1">{stop.name}</Typography>
                                  <Typography className={classes.address}>
                                    {`${stop.street} ${stop.street_number}, ${stop.zipcode} ${stop.city}`}
                                  </Typography>
                                </div>

                                {stop.Orders && stop.Orders.length > 0 && (
                                  <Chip
                                    icon={<LocalShippingIcon style={{ fontSize: 14 }} />}
                                    label={`${stop.Orders.length} ${t("Orders")}`}
                                    size="small"
                                    style={{
                                      backgroundColor: "#444",
                                      color: "#fff",
                                      height: "24px",
                                      fontSize: "0.75rem",
                                      borderRadius: "16px",
                                    }}
                                  />
                                )}
                              </AccordionSummary>
                            </Accordion>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("Cancel")}
        </Button>
        {hasReorderPermission && !isRouteStarted && (
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading || !route || !reorderEnabled}
            style={{
              backgroundColor: !reorderEnabled ? "#6F9CEB" : undefined,
              opacity: !reorderEnabled ? 0.6 : 1,
            }}
          >
            {t("Save")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReorderStopsModal;
